import { createStyles, CSSObject, Loader, UnstyledButton, UnstyledButtonProps } from "@mantine/core";
import { PolymorphicComponentProps } from "@mantine/utils";
import React from "react";
import { Flex, IconV2 } from "../..";
import { IconColor, IconFn } from "../../icons/createIcon";

export type RpiButtonVariant = "spark" | "spark-outline" | "spark-ghost" | "mellow" | "brand-outline" | "brand";

export type RpiButtonSize = "large" | "small";

interface RpiButtonProps {
  variant?: RpiButtonVariant;
  size?: RpiButtonSize;
  shadow?: boolean;
  icon?: IconFn<IconColor, Record<string, unknown>>;
  width?: "full" | "fit" | number;
  isLoading?: boolean;
  trailingIcon?: IconFn<IconColor, Record<string, unknown>> | boolean;
}

const useRpiButtonStyles = createStyles<
  "root" | "variant",
  Required<Omit<RpiButtonProps, "icon" | "isLoading" | "trailingIcon" | "noShadow" | "width" | "fullWidth">> &
    Pick<RpiButtonProps, "width">
>((theme, { variant, size, shadow, width }) => {
  const variantCss: Record<Exclude<RpiButtonProps["variant"], undefined>, CSSObject> = {
    spark: {
      color: theme.colors.sky[5],
      background: theme.colors.spark[5],
      borderColor: theme.colors.spark[5],
      "&:hover": {
        color: theme.colors.spark[5],
        background: theme.colors.sky[5],
        borderColor: theme.colors.spark[5],
      },
      "&:disabled": {
        color: theme.colors.sky[5],
        background: theme.colors.spark[3],
        borderColor: theme.colors.spark[3],
      },
    },
    "spark-outline": {
      color: theme.colors.spark[5],
      background: theme.colors.sky[5],
      borderColor: theme.colors.spark[5],
      "&:hover": {
        color: theme.colors.sky[5],
        background: theme.colors.spark[5],
        borderColor: theme.colors.spark[5],
      },
      "&:disabled": {
        color: theme.colors.spark[3],
        background: theme.colors.sky[5],
        borderColor: theme.colors.spark[3],
      },
    },
    "spark-ghost": {
      color: theme.colors.spark[5],
      background: "transparent",
      borderColor: "transparent",
      "&:disabled": {
        color: theme.colors.spark[3],
      },
    },
    mellow: {
      color: theme.colors.brand[5],
      background: theme.colors.mellow[5],
      borderColor: theme.colors.mellow[5],
      "&:hover": {
        color: theme.colors.mellow[5],
        background: theme.colors.brand[5],
        borderColor: theme.colors.brand[5],
      },
      "&:disabled": {
        color: theme.colors.brand[4],
        background: theme.colors.brand[3],
        borderColor: theme.colors.brand[3],
      },
    },
    "brand-outline": {
      color: theme.colors.brand[5],
      background: theme.colors.sky[3],
      borderColor: theme.colors.brand[5],
      "&:hover": {
        color: theme.colors.sky[5],
        background: theme.colors.brand[5],
        borderColor: theme.colors.brand[5],
      },
      "&:disabled": {
        color: theme.colors.brand[3],
        background: theme.colors.sky[5],
        borderColor: theme.colors.brand[3],
      },
    },
    brand: {
      color: theme.colors.sky[5],
      background: theme.colors.brand[5],
      "&:hover": {
        background: theme.colors.brand[4],
      },
      "&:disabled": {
        color: theme.colors.text[3],
        background: theme.colors.sky[8],
      },
    },
  };

  let buttonWidth: CSSObject["width"];
  if (width) {
    if (width === "full") buttonWidth = "100%";
    else if (width === "fit") buttonWidth = "fit-content";
    else buttonWidth = width;
  } else {
    buttonWidth = size === "large" ? 410 : 132;
  }

  return {
    root: {
      ...theme.other.fontTypes.button,
      fontWeight: theme.other.fontWeights.bold,
      boxSizing: "border-box",
      lineHeight: "17px",
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      paddingTop: 14,
      paddingBottom: 14,
      paddingLeft: size === "large" ? 28 : 22,
      paddingRight: size === "large" ? 28 : 22,
      width: buttonWidth,
      height: 56,
      outline: "none !important",
      textAlign: "center",
      userSelect: "none",
      borderRadius: "32px",
      borderWidth: 2,
      borderStyle: "solid",
      transition: "color 250ms, background 250ms, border 250ms",
      ...(shadow && { boxShadow: theme.other.boxShadows.button }),
      "&:hover": {
        ...(shadow && { boxShadow: theme.other.boxShadows.buttonHover }),
      },
      "&:active": {
        transform: "translateY(1px)",
      },
      "&:disabled": {
        cursor: "default",
        boxShadow: "none",
        transform: "none",
      },
    },
    variant: variantCss[variant],
  };
});

export function RpiButton<C = "button">({
  variant = "spark",
  size = "small",
  shadow,
  icon,
  width,
  className,
  children,
  isLoading,
  trailingIcon,
  ...props
}: PolymorphicComponentProps<C, UnstyledButtonProps & RpiButtonProps>) {
  const { classes, cx } = useRpiButtonStyles({ variant, size, shadow: !!shadow, width });

  const childrenMemo = React.useMemo(() => {
    const updatedChildren = isLoading ? (
      <Flex.Row align="center" gap={8}>
        <Loader size={18} color="currentColor" />
        <div children={children} />
      </Flex.Row>
    ) : (
      children
    );

    if (!trailingIcon && !icon) return updatedChildren;

    return (
      <Flex.Row align="center" justify={width === "full" ? "center" : "space-between"} gap={10} fullWidth>
        {icon && (
          <div
            children={React.createElement(icon, {
              width: 14,
              height: 14,
              color: "currentColor",
            })}
          />
        )}
        {updatedChildren}
        {trailingIcon && (
          <div
            children={React.createElement(typeof trailingIcon === "boolean" ? IconV2.Arrow : trailingIcon, {
              width: 14,
              height: 14,
              color: "currentColor",
            })}
          />
        )}
      </Flex.Row>
    );
  }, [children, icon, isLoading, trailingIcon, width]);

  return (
    // @ts-expect-error legacy type issues
    <UnstyledButton
      className={cx(classes.root, classes.variant, className)}
      children={childrenMemo}
      {...props}
      {...(isLoading && { disabled: true })}
    />
  );
}
